import type { ConfidenceThresholds } from '../creatives/confidence'
import type { Axis } from './pool'
import { UNASSIGNED } from '../creatives/partition'
import type { Lang } from '../i18n'
import { DICT, type Dict } from '../i18n'
import { fill, plural } from '../creatives/format'
import { formatRange, type Period } from '../lib/period'

/**
 * The footnote's derivation as a pure module (doc 03 step 7): the ordered buckets, the
 * thresholds, the period and the language in, the interpolation variables out. The
 * dictionary template is applied by the caller (`Footnote.tsx`), so the sentence cannot
 * go stale when the data moves - the failure the hand-written spike footnote suffered.
 *
 * The clause carries no superlative and no editorial remark about the specific data: the
 * data cannot know why a bucket is unassigned, and a sentence about "the largest bucket"
 * is false the day the fixture changes.
 */

/**
 * One bucket as the footnote sees it: the pooled conversions and the display name the
 * caller resolved from `dimensions` - or the unassigned label for the sentinel bucket.
 * Names are response data, so they live at the call site; the derivation here is pure.
 */
export interface FootnoteBucket {
  id: string
  name: string
  conversions: number | null
}

export interface FootnoteVars {
  /** The list clause, ready for `%list`: the clearing buckets, or the none-clear-yet line. */
  list: string
  /** The audiences joined with the language's conjunction and quotes, ready for `%aud`. */
  aud: string
  /**
   * The unassigned clause with its leading space, ready for `%un`, or `''` when no
   * unassigned bucket exists. Axis-specific: the hypothesis axis and the technique axis
   * name their own absence, and the audience axis has none.
   */
  un: string
  /**
   * The window clause, ready for `%win`. A preset trails to the last complete day and
   * reads as the last N days; a range may sit anywhere in the past, so it names its dates.
   */
  win: string
}

export function footnoteVariables(
  axis: Axis,
  buckets: FootnoteBucket[],
  audiences: string[],
  thresholds: ConfidenceThresholds,
  period: Period,
  lang: Lang,
): FootnoteVars {
  const t = DICT[lang]
  const floor = thresholds.provisionalAt
  const unit = plural(floor, lang, { one: t.unitOne, few: t.unitFew, many: t.unitMany })

  const quoted = (name: string) => `${t.quoteOpen}${name}${t.quoteClose}`
  const clearers = buckets
    .filter((bucket) => (bucket.conversions ?? 0) >= floor)
    .sort((left, right) => (right.conversions ?? 0) - (left.conversions ?? 0))
    .map((bucket) => `${quoted(bucket.name)} (n=${bucket.conversions})`)
  // "A, B and C": the conjunction only before the last name, commas between the rest.
  const joined =
    clearers.length <= 1
      ? clearers.join('')
      : `${clearers.slice(0, -1).join(', ')}${t.andJoin}${clearers[clearers.length - 1]}`
  const list =
    clearers.length > 0
      ? fill(t.footList, { n: floor, u: unit, list: joined })
      : fill(t.footNone, { n: floor, u: unit })

  const unassigned = buckets.find((bucket) => bucket.id === UNASSIGNED)
  const un = unassigned
    ? fill(axis === 'technique' ? t.unassignedFootVisual : t.unassignedFootHyp, { v: unassigned.name })
    : ''

  return {
    list,
    aud: audiences.map(quoted).join(t.andJoin),
    un,
    win: period.kind === 'preset'
      ? fill(t.footWindowLast, { p: period.days })
      // A Russian short month carries its own full stop (`авг.`), which ends the sentence.
      : fill(t.footWindowRange, { r: formatRange(period.from, period.to, lang) }).replace(/\.\.$/, '.'),
  }
}

/**
 * The footnote sentence.
 *
 * The audience clause names the audiences the account pools into, so it is dropped whole
 * when there are none rather than interpolating an empty list into it: `%aud` would then
 * leave a dangling "- one creative can sit in several audience buckets", which is both
 * ungrammatical and about nothing. `aud === ''` is exactly the empty list, because a quoted
 * label is at least the two quote characters.
 */
export function footnoteText(t: Dict, vars: FootnoteVars): string {
  const template = vars.aud === '' ? t.hypFootNoAud : t.hypFoot
  return fill(template, { list: vars.list, aud: vars.aud, un: vars.un, win: vars.win })
}